import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ActiveCity {
  city: string;
  listingCount: number;
  lat: number | null;
  lng: number | null;
}

// Known accurate city centres (avoids skew from bad listing coordinates)
const KNOWN_CENTERS: Record<string, { lat: number; lng: number }> = {
  Edmonton: { lat: 53.5461, lng: -113.4938 },
  Calgary: { lat: 51.0447, lng: -114.0719 },
  Airdrie: { lat: 51.2917, lng: -114.0144 },
  Cochrane: { lat: 51.1892, lng: -114.4686 },
  'Spruce Grove': { lat: 53.545, lng: -113.9008 },
  Chestermere: { lat: 51.0378, lng: -113.8225 },
  Leduc: { lat: 53.2594, lng: -113.5492 },
  'St. Albert': { lat: 53.6305, lng: -113.6256 },
  'Sherwood Park': { lat: 53.5169, lng: -113.3161 },
  Beaumont: { lat: 53.3517, lng: -113.4158 },
  Okotoks: { lat: 50.7256, lng: -113.9749 },
};

export const DEFAULT_CITY = 'Edmonton';

interface RpcRow {
  city: string;
  listing_count: number;
  avg_lat: number | null;
  avg_lng: number | null;
}

export function useCities(minCount = 10) {
  return useQuery({
    queryKey: ['active-cities', minCount],
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    gcTime: 1000 * 60 * 60 * 24,
    queryFn: async (): Promise<ActiveCity[]> => {
      const { data, error } = await supabase.rpc('get_active_cities', {
        min_count: minCount,
      });

      if (error) throw error;

      return ((data ?? []) as RpcRow[]).map((row) => {
        const known = KNOWN_CENTERS[row.city];
        return {
          city: row.city,
          listingCount: Number(row.listing_count) || 0,
          lat: known?.lat ?? row.avg_lat ?? null,
          lng: known?.lng ?? row.avg_lng ?? null,
        };
      });
    },
  });
}

export function getCityCenter(
  city: string,
  cities?: ActiveCity[]
): { lat: number; lng: number } {
  const known = KNOWN_CENTERS[city];
  if (known) return known;

  const match = cities?.find((c) => c.city === city);
  if (match?.lat != null && match?.lng != null) {
    return { lat: match.lat, lng: match.lng };
  }

  return KNOWN_CENTERS[DEFAULT_CITY];
}
